import { Routes } from '@angular/router';
import { authGuard, guestGuard, hasRight, jivuOverlayGuard } from './core/auth/guards/auth.guard';
import { nativeRootRedirectGuard } from './core/auth/guards/native-root.guard';
import { Rights } from './core/constants/rights.constants';
import { MainLayoutComponent } from './layout/shell/main-layout/main-layout.component';
import { AuthLayoutComponent } from './layout/shell/auth-layout/auth-layout.component';
import { publicStoreRouteMatcher } from './public-store-route-matcher';

export { publicStoreRouteMatcher } from './public-store-route-matcher';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canMatch: [nativeRootRedirectGuard],
    loadComponent: () =>
      import('./core/auth/pages/native-root-redirect/native-root-redirect.page').then(
        (m) => m.NativeRootRedirectPage,
      ),
  },
  {
    path: 'jivu-shell',
    canActivate: [authGuard, hasRight(Rights.JivuTalkShell_Access)],
    loadComponent: () =>
      import('./features/JivuShell/pages/jivu-shell.page').then((m) => m.JivuShellPage),
  },
  {
    path: 'jivu-overlay',
    canActivate: [jivuOverlayGuard],
    loadComponent: () =>
      import('./features/JivuShell/pages/jivu-overlay.page').then((m) => m.JivuOverlayPage),
  },
  {
    path: '',
    component: AuthLayoutComponent,
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('./core/landing/pages/landing-page').then((m) => m.LandingPageComponent),
      },
      {
        path: 'workflow',
        redirectTo: '',
        pathMatch: 'full',
      },
      {
        path: 'ai-keys',
        redirectTo: '',
        pathMatch: 'full',
      },
      {
        path: 'pricing',
        redirectTo: '',
        pathMatch: 'full',
      },
      {
        path: 'faq',
        loadComponent: () =>
          import('./core/landing/pages/landing-page').then((m) => m.LandingPageComponent),
        data: { initialSection: 'faq' },
      },
      {
        path: 'about-us',
        loadComponent: () =>
          import('./core/legal/pages/legal-page').then((m) => m.LegalPageComponent),
        data: { page: 'about' },
      },
      {
        path: 'about',
        redirectTo: 'about-us',
        pathMatch: 'full',
      },
      {
        path: 'contact-us',
        loadComponent: () =>
          import('./core/legal/pages/legal-page').then((m) => m.LegalPageComponent),
        data: { page: 'contact' },
      },
      {
        path: 'contact',
        redirectTo: 'contact-us',
        pathMatch: 'full',
      },
      {
        path: 'privacy',
        loadComponent: () =>
          import('./core/legal/pages/legal-page').then((m) => m.LegalPageComponent),
        data: { page: 'privacy' },
      },
      {
        path: 'terms',
        loadComponent: () =>
          import('./core/legal/pages/legal-page').then((m) => m.LegalPageComponent),
        data: { page: 'terms' },
      },
      {
        path: 'cancellation-refund-policy',
        loadComponent: () =>
          import('./core/legal/pages/legal-page').then((m) => m.LegalPageComponent),
        data: { page: 'refund' },
      },
      {
        path: 'refund-policy',
        redirectTo: 'cancellation-refund-policy',
        pathMatch: 'full',
      },
      {
        path: 'return-policy',
        loadComponent: () =>
          import('./core/legal/pages/legal-page').then((m) => m.LegalPageComponent),
        data: { page: 'return' },
      },
      {
        path: 'shipping-policy',
        loadComponent: () =>
          import('./core/legal/pages/legal-page').then((m) => m.LegalPageComponent),
        data: { page: 'shipping' },
      },
      {
        path: 'digital-delivery-policy',
        redirectTo: 'shipping-policy',
        pathMatch: 'full',
      },
      {
        path: 'ownership-statement',
        loadComponent: () =>
          import('./core/legal/pages/legal-page').then((m) => m.LegalPageComponent),
        data: { page: 'ownership' },
      },
      {
        path: 'google-oauth-disclosure',
        loadComponent: () =>
          import('./core/legal/pages/legal-page').then((m) => m.LegalPageComponent),
        data: { page: 'oauth' },
      },
      {
        matcher: publicStoreRouteMatcher,
        loadComponent: () =>
          import('./features/Ecommerce/pages/public-storefront.page').then(
            (m) => m.PublicStorefrontPage,
          ),
      },
      {
        path: 'login',
        canActivate: [guestGuard],
        loadComponent: () => import('./core/auth/pages/login/login.page').then((m) => m.LoginPage),
      },
      {
        path: 'register',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('./core/auth/pages/register/register.page').then((m) => m.RegisterPage),
      },
      {
        path: 'confirm-email',
        loadComponent: () =>
          import('./core/auth/pages/confirm-email/confirm-email.page').then(
            (m) => m.ConfirmEmailPage,
          ),
      },
      {
        path: 'forgot-password',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('./core/auth/pages/forgot-password/forgot-password.page').then(
            (m) => m.ForgotPasswordPage,
          ),
      },
      {
        path: 'reset-password',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('./core/auth/pages/reset-password/reset-password.page').then(
            (m) => m.ResetPasswordPage,
          ),
      },
    ],
  },
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: 'onboarding',
        loadComponent: () =>
          import('./core/onboarding/pages/plan-selection/plan-selection.page').then(
            (m) => m.PlanSelectionPage,
          ),
      },
      {
        path: 'payment-result',
        loadComponent: () =>
          import('./core/onboarding/pages/payment-result/payment-result.page').then(
            (m) => m.PaymentResultPage,
          ),
      },
      {
        path: 'edit',
        loadComponent: () =>
          import('./features/creator-home/creator-home.page').then(
            (m) => m.CreatorHomePage,
          ),
      },
      {
        path: 'search',
        loadComponent: () =>
          import('./features/creator-search/creator-search.page').then(
            (m) => m.CreatorSearchPage,
          ),
      },
      {
        path: 'dashboard',
        pathMatch: 'full',
        redirectTo: 'edit',
      },
      {
        path: 'templates',
        loadComponent: () =>
          import('./features/templates/templates.page').then((m) => m.TemplatesPage),
      },
      {
        path: 'jivu-ai',
        loadComponent: () =>
          import('./features/creator-home/jivu-ai-landing.page').then(
            (m) => m.JivuAiLandingPage,
          ),
      },
      {
        path: 'projects',
        loadChildren: () =>
          import('./features/projects/projects.routes').then((m) => m.PROJECTS_ROUTES),
      },
      {
        path: 'me',
        pathMatch: 'full',
        loadComponent: () =>
          import('./features/me/pages/me.page').then((m) => m.MePage),
      },
      {
        path: 'me/settings',
        pathMatch: 'full',
        redirectTo: 'me/settings/profile',
      },
      {
        path: 'me/settings/:section',
        loadComponent: () =>
          import('./features/me/pages/me-settings.page').then((m) => m.MeSettingsPage),
      },
      {
        path: 'video-gen',
        canActivate: [hasRight(Rights.VideoGen_View)],
        loadChildren: () => import('./features/VideoGen/video-gen.routes').then((m) => m.routes),
      },
      {
        path: 'pipeline',
        canActivate: [hasRight(Rights.Pipeline_View)],
        loadChildren: () => import('./features/Pipeline/pipeline.routes').then((m) => m.routes),
      },
      {
        path: 'jivu-command',
        canActivate: [hasRight(Rights.JivuCommand_Access)],
        loadComponent: () =>
          import('./features/JivuCommand/pages/jivu-command.page').then((m) => m.JivuCommandPage),
      },
      {
        path: 'post-gen',
        canActivate: [hasRight(Rights.PostGen_View)],
        loadChildren: () => import('./features/PostGen/post-gen.routes').then((m) => m.routes),
      },
      {
        path: 'review',
        canActivate: [hasRight(Rights.Review_View)],
        loadChildren: () => import('./features/Review/review.routes').then((m) => m.routes),
      },
      {
        path: 'super-editor',
        canActivate: [hasRight(Rights.Review_View)],
        loadChildren: () =>
          import('./features/super-editor/super-editor.routes').then((m) => m.SUPER_EDITOR_ROUTES),
      },
      {
        path: 'quick-edit',
        data: { preload: true },
        loadChildren: () =>
          import('./features/quick-edit/quick-edit.routes').then((m) => m.QUICK_EDIT_ROUTES),
      },
      {
        path: 'quick-tools',
        loadChildren: () =>
          import('./features/quick-tools/quick-tools.routes').then((m) => m.QUICK_TOOLS_ROUTES),
      },
      {
        // NOTE: VideoGenNew_View is a frontend-only key for now. The backend does not yet
        // grant this right; gate it the same way /super-editor is gated (Review_View)
        // until the backend grants VideoGenNew_View to the appropriate plans.
        // TODO right: switch canActivate guard to hasRight(Rights.VideoGenNew_View) once
        // the backend grants that key.
        path: 'video-gen-new',
        canActivate: [hasRight(Rights.VideoGen_Manage)],
        loadChildren: () =>
          import('./features/VideoGenNew/video-gen-new.routes').then((m) => m.VIDEO_GEN_NEW_ROUTES),
      },
      {
        path: 'image-editor',
        canActivate: [hasRight(Rights.Review_View)],
        loadChildren: () =>
          import('./features/image-editor/image-editor.routes').then((m) => m.IMAGE_EDITOR_ROUTES),
      },
      {
        path: 'social',
        loadChildren: () => import('./features/Social/social.routes').then((m) => m.routes),
      },
      {
        path: 'ecommerce',
        canActivate: [hasRight(Rights.Ecommerce_View)],
        loadChildren: () => import('./features/Ecommerce/ecommerce.routes').then((m) => m.routes),
      },
      {
        path: 'memory',
        pathMatch: 'full',
        redirectTo: 'jivu-ai',
      },
      {
        path: 'settings',
        canActivate: [hasRight(Rights.Settings_View)],
        loadChildren: () => import('./features/Settings/settings.routes').then((m) => m.routes),
      },
      {
        path: 'upgrade',
        loadComponent: () => import('./features/Upgrade/upgrade.page').then((m) => m.UpgradePage),
      },
    ],
  },
];
